import { CreditCard } from '@/types';
import { logger } from '@/utils/logger';

export interface CardsState {
  cards: CreditCard[];
  isLoading: boolean;
  error: string | null;
}

export type CardsAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CARDS'; payload: CreditCard[] }
  | { type: 'ADD_CARD'; payload: CreditCard }
  | { type: 'UPDATE_CARD'; payload: { id: string; updates: Partial<CreditCard> } }
  | { type: 'DELETE_CARD'; payload: string }
  | { type: 'UPDATE_CARD_USAGE'; payload: { cardId: string; amount: number } }
  | { type: 'CLEAR_CARDS' };

export const initialState: CardsState = {
  cards: [],
  isLoading: false,
  error: null,
};

export function cardsReducer(
  state: CardsState,
  action: CardsAction
): CardsState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_CARDS':
      return { 
        ...state, 
        cards: action.payload,
        isLoading: false,
        error: null
      };

    case 'ADD_CARD':
      logger.debug('Adding card to state', { id: action.payload.id });
      return {
        ...state,
        cards: [...state.cards, action.payload],
        error: null,
      };

    case 'UPDATE_CARD': {
      const updated = state.cards.map(c =>
        c.id === action.payload.id ? { ...c, ...action.payload.updates } : c
      );
      logger.debug('Updated card in state', { id: action.payload.id });
      return { ...state, cards: updated, error: null };
    }

    case 'DELETE_CARD': {
      const filtered = state.cards.filter(c => c.id !== action.payload);
      logger.debug('Deleted card from state', { id: action.payload });
      return { ...state, cards: filtered, error: null };
    }

    case 'UPDATE_CARD_USAGE': {
      const updated = state.cards.map(card => {
        if (card.id === action.payload.cardId) {
          const newUsed = Math.max(0, card.used + action.payload.amount);
          return { ...card, used: newUsed };
        }
        return card;
      });
      return { ...state, cards: updated };
    }

    case 'CLEAR_CARDS':
      return { ...state, cards: [], error: null };

    default:
      return state;
  }
}
